<template>
    <FiltersModal
        :is-open
        :route-reset="filterRoutes.routeReset"
        :result-count="resultCount"
        :active-filter-count="chips.length"
        :fetching="loading"
        @close="$emit('close')"
    >
        <template #indicator>
            <ProgressIndicatorLinear :loading />
        </template>

        <template #chips>
            <FilterChips
                v-qa="'filter-chips-filter'"
                :chips
                :route-reset="filterRoutes.routeReset"
            />
        </template>

        <template #filters>
            <FilterAccordion
                :title="t('catalogue.filters.price')"
                :count="values.price?.min && values.price?.max ? 1 : 0"
                hide-count
            >
                <FilterPriceRange
                    :disabled
                    :filter="filters.price"
                    :formatter
                    :value="values.price"
                    @update="priceUpdated"
                />
            </FilterAccordion>

            <FilterAccordion
                v-qa="'filter-category'"
                :title="t('catalogue.filters.categories')"
                :count="values.categories?.length"
            >
                <FilterLabel
                    :disabled
                    :filter="filters.categories"
                    :get-add-link="filterRoutes.getRouteAddCategory"
                    :get-remove-link="filterRoutes.getRouteRemoveCategory"
                    :get-route-add="filterRoutes.getRouteAddCategory"
                    :get-route-remove="filterRoutes.getRouteRemoveCategory"
                    :selected="values.categories ?? []"
                />
            </FilterAccordion>

            <FilterAccordion
                v-qa="'filter-metal'"
                :title="t('catalogue.filters.metal')"
                :count="values.primaryMetalCodes?.length"
            >
                <FilterMetal
                    :disabled
                    :filter="filters.primaryMetals"
                    :get-add-metal-route="filterRoutes.getRouteAddMetal"
                    :get-route-add-metal="filterRoutes.getRouteAddMetal"
                    :get-route-remove-metal="filterRoutes.getRouteRemoveMetal"
                    :remove-metal-route="filterRoutes.getRouteRemoveMetal"
                    :selected="values.primaryMetalCodes ?? []"
                />
            </FilterAccordion>

            <FilterAccordion
                v-qa="'filter-ring-style'"
                :title="t('catalogue.filters.ring_style')"
                :count="values.ringStyles?.length"
            >
                <FilterLabel
                    :disabled
                    :filter="filters.ringStyles"
                    :get-add-link="filterRoutes.getRouteAddRingStyle"
                    :get-remove-link="filterRoutes.getRouteRemoveRingStyle"
                    :get-route-add="filterRoutes.getRouteAddRingStyle"
                    :get-route-remove="filterRoutes.getRouteRemoveRingStyle"
                    :selected="values.ringStyles ?? []"
                />
            </FilterAccordion>
        </template>
    </FiltersModal>
</template>

<script lang="ts" setup>
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import type {
    CatalogueWeddingRingFilterInput,
    MetalCode,
    WeddingRingCategoryLabelCode,
    WeddingRingFiltersFragment,
    WeddingRingStyleLabelCode,
} from '@/graphql/api'

import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'

import FiltersModal from '@/components/ui/filter/FiltersModal.vue'
import FilterPriceRange from '@/components/ui/filter/filters/FilterPriceRange.vue'
import FilterMetal from '@/components/ui/filter/filters/FilterMetal.vue'
import FilterLabel from '@/components/ui/filter/filters/FilterLabel.vue'
import FilterChips, {
    type FilterChip,
} from '@/components/ui/filter/FilterChips.vue'
import FilterAccordion from '@/components/ui/filter/FilterAccordion.vue'
import ProgressIndicatorLinear from '@/components/ui/indicators/ProgressIndicatorLinear.vue'
import type { InputRangeModel } from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'
import { useMoneyFormatter } from '@/plugins/money-formatter.ts'

const props = defineProps<{
    filters: WeddingRingFiltersFragment
    values: CatalogueWeddingRingFilterInput
    resultCount: number
    isOpen: boolean
    filterRoutes: {
        routeReset: ButtonRouteRelative
        getRoutePrice: (value: InputRangeModel) => ButtonRouteRelative
        getRouteAddMetal: (metal: MetalCode) => ButtonRouteRelative
        getRouteRemoveMetal: () => ButtonRouteRelative
        getRouteAddCategory: (
            category: WeddingRingCategoryLabelCode
        ) => ButtonRouteRelative
        getRouteRemoveCategory: (
            category: WeddingRingCategoryLabelCode
        ) => ButtonRouteRelative
        getRouteAddRingStyle: (
            ringStyle: WeddingRingStyleLabelCode
        ) => ButtonRouteRelative
        getRouteRemoveRingStyle: (
            ringStyle: WeddingRingStyleLabelCode
        ) => ButtonRouteRelative
    }
    disabled: boolean
    chips: FilterChip[]
    loading: boolean
}>()

defineEmits<{
    'close': []
}>()

const { t } = useI18n()

const router = useRouter()
const priceUpdated = (values: InputRangeModel) => {
    router.push(props.filterRoutes.getRoutePrice(values))
}

const formatter = useMoneyFormatter()
</script>
